import { Component, ErrorInfo, ReactNode } from 'react';
import { isLikelyDynamicImportFailure, normalizeError, reportError } from '../lib/errorManager';
import { trackAppError } from '../lib/analytics';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** Bump route lazy retry key so failed dynamic imports (e.g. offline) fetch chunks again. */
  onRetry?: () => void;
}

interface State {
  hasError: boolean;
  /** Used to choose reload vs in-app lazy retry (see isLikelyDynamicImportFailure). */
  errorMessage: string | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  static getDerivedStateFromError(error: unknown): State {
    const err = normalizeError(error);
    return { hasError: true, errorMessage: err.message };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const err = normalizeError(error);
    reportError(err, {
      source: 'react_error_boundary',
      reactComponentStack: errorInfo.componentStack,
    });
    trackAppError({
      error_type: 'react_render',
      error_message: err.message || 'Unknown render error',
      user: null,
    });
  }

  handleReset = () => {
    const msg = this.state.errorMessage ?? '';
    if (typeof window !== 'undefined' && isLikelyDynamicImportFailure(msg)) {
      window.location.reload();
      return;
    }
    if (this.props.onRetry) {
      this.props.onRetry();
      return;
    }
    this.setState({ hasError: false, errorMessage: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback != null) {
        return this.props.fallback;
      }
      const chunkFailure =
        !!this.state.errorMessage && isLikelyDynamicImportFailure(this.state.errorMessage);
      return (
        <div
          className="public-marketing-page"
          style={{
            minHeight: 'calc(100vh - 80px)',
            background: 'var(--marketing-page-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px 24px',
          }}
        >
          <div
            className="marketing-panel"
            style={{
              textAlign: 'center',
              padding: '48px 32px',
              maxWidth: '440px',
              width: '100%',
            }}
          >
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '12px', color: 'var(--marketing-heading)' }}>
              Something went wrong
            </h2>
            <p style={{ color: 'var(--marketing-muted)', marginBottom: '24px', lineHeight: 1.55 }}>
              {chunkFailure
                ? 'This page could not load (often after being offline or losing connection). Use the button below to reload and try again.'
                : 'An unexpected error occurred. Please try again.'}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
              <button
                type="button"
                onClick={this.handleReset}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '14px 32px',
                  background: '#f8fafc',
                  color: 'var(--marketing-heading)',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  cursor: 'pointer',
                }}
              >
                {chunkFailure ? 'Reload page' : 'Try again'}
              </button>
              <a
                href="/"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '14px 32px',
                  background: 'var(--gradient-1)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '600',
                  fontSize: '16px',
                  textDecoration: 'none',
                }}
              >
                Go to Home
              </a>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
